/**
 * Pagination for a Bexio workspace table.
 *
 * Offset-based, matching the list endpoints: both accept `limit` (1..200) and
 * `offset` and return a `total` counted over the SAME filters, so the range
 * below is arithmetic on real numbers rather than an estimate.
 *
 * Numbered page buttons deliberately, as in the design reference — with a total
 * this is honest, and it beats prev/next for jumping. The window is capped so a
 * few thousand rows cannot render a few hundred buttons.
 */
import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/** How many numbered buttons to show around the current page. */
const WINDOW = 5;

const PAGE_SIZES = [10, 25, 50];

function pageWindow(current, last) {
  if (last <= WINDOW) {
    return Array.from({ length: last }, (_, i) => i + 1);
  }
  const half = Math.floor(WINDOW / 2);
  let start = Math.max(1, current - half);
  const end = Math.min(last, start + WINDOW - 1);
  start = Math.max(1, end - WINDOW + 1);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

export default function BexioTablePagination({
  total,
  offset,
  limit,
  onOffsetChange,
  onLimitChange,
  noun = "Einträge",
  busy = false,
}) {
  const safeTotal = Number(total) || 0;
  if (safeTotal === 0) return null;

  const last = Math.max(1, Math.ceil(safeTotal / limit));
  const current = Math.min(last, Math.floor(offset / limit) + 1);
  const from = safeTotal === 0 ? 0 : offset + 1;
  const to = Math.min(offset + limit, safeTotal);

  const go = (page) => {
    const clamped = Math.min(Math.max(1, page), last);
    onOffsetChange((clamped - 1) * limit);
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3">
      <span className="text-xs text-muted-foreground">
        Zeige {from} bis {to} von {safeTotal} {noun}
      </span>

      <div className="flex flex-wrap items-center gap-3">
        {onLimitChange ? (
          <label className="inline-flex items-center gap-2 text-xs text-muted-foreground">
            Zeilen pro Seite:
            <select
              aria-label="Zeilen pro Seite"
              value={limit}
              onChange={(e) => onLimitChange(Number(e.target.value))}
              className="rounded-lg border border-border bg-background px-2 py-1 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {PAGE_SIZES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <nav aria-label="Seitennavigation" className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Vorherige Seite"
            onClick={() => go(current - 1)}
            disabled={busy || current <= 1}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronLeft aria-hidden="true" className="h-4 w-4" />
          </button>

          {pageWindow(current, last).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => go(p)}
              disabled={busy}
              aria-label={`Seite ${p}`}
              aria-current={p === current ? "page" : undefined}
              className={`inline-flex h-8 min-w-8 items-center justify-center rounded-lg border px-2 text-xs font-medium tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                p === current
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border text-foreground hover:bg-muted"
              }`}
            >
              {p}
            </button>
          ))}

          <button
            type="button"
            aria-label="Nächste Seite"
            onClick={() => go(current + 1)}
            disabled={busy || current >= last}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronRight aria-hidden="true" className="h-4 w-4" />
          </button>
        </nav>
      </div>
    </div>
  );
}
